import type { SimilarGame } from "@/types/api";
import { useLang } from "@/i18n/LangContext";
import { Link } from "react-router-dom";

interface SimilarGamesCarouselProps {
  similarGames: SimilarGame[];
}

export default function SimilarGamesCarousel({ similarGames }: SimilarGamesCarouselProps) {
  const { t } = useLang();

  if (similarGames.length === 0) return null;

  return (
    <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-6 mb-6">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z" />
            <path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t.gameDetail.similarGames}</h2>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-[#3a506e]">
        {similarGames.map((game) => (
          <Link
            key={game.appid}
            to={`/game/${game.appid}`}
            className="shrink-0 w-[200px] group"
          >
            <div className="relative aspect-[46/21] rounded-xl overflow-hidden bg-hover-bg dark:bg-background mb-2">
              <img
                src={`https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${game.appid}/header.jpg`}
                alt={game.name}
                width={460}
                height={215}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
                decoding="async"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              {game.discount_percent > 0 && (
                <span className="absolute top-1.5 right-1.5 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md">
                  -{game.discount_percent}%
                </span>
              )}
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors">
              {game.name}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              {game.original_price && (
                <span className="text-xs text-gray-400 dark:text-sub-text line-through">
                  {game.original_price}
                </span>
              )}
              <span className="text-xs font-bold text-gray-900 dark:text-white">
                {game.price || t.gameDetail.noPriceInfo}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
